// Inhaber-Verwaltung: alle Mandanten, Tarif ändern, Zugang einfrieren,
// Passwort zurücksetzen, Angebote schreiben, Daten exportieren.
import { apiUrl } from '../../../gemeinsam/lib/plattform';
import { Erfolgsmeldung } from '../../../gemeinsam/komponenten/Meldungen';
import { useState } from 'react';
import { api, ApiFehler, token } from '../../../gemeinsam/lib/api';
import { useDaten, euro, datum } from '../../../gemeinsam/lib/daten';
import type { Angebot, Benutzer, Mandant } from '../../../gemeinsam/lib/typen';
import KiKontingent from '../../ki/komponenten/KiKontingent';
import MandantAnschreiben from '../komponenten/MandantAnschreiben';
import {
  Abzeichen, Auswahl, Blickfang, Feld, Hinweis, Karte, KartenKopf, Knopf, LeerZustand, Spinner,
} from '../../../gemeinsam/komponenten/Basis';

const TARIFE = [
  { id: 'starter', name: 'Starter', preis: 19 },
  { id: 'professional', name: 'Professional', preis: 49 },
  { id: 'enterprise', name: 'Enterprise', preis: 99 },
];

type Statistik = { tenants?: number; users?: number; mrr?: number };

type Sicherung = {
  id: string;
  tenantId: string;
  tenantName: string;
  grund: string;
  umfang: Record<string, number>;
  groesse: number;
  format?: number;
  createdAt: string;
  laeuftAbAm: string;
};

const letzterTag = (s: string) => new Date(Date.parse(s) - 1).toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin' });

/** Datenkopie nach Kündigung: Format wählen und freigeben — der Kunde lädt sie mit seinem Passwort. */
function KopieFreigabe({ mandant, beiErfolg }: { mandant: Mandant; beiErfolg: (text: string) => void | Promise<void> }) {
  const k = mandant.kopie!;
  const [format, setFormat] = useState<'lesbar' | 'wiederherstellung'>(k.freigabe?.format ?? 'lesbar');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const tagDe = (s: string) => new Date(s).toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin' });

  async function freigeben() {
    setLaeuft(true);
    setFehler(null);
    try {
      await api(`/billing/admin/tenants/${mandant.id}/datenkopie`, { methode: 'POST', daten: { format } });
      await beiErfolg(`${mandant.name}: Datenkopie freigegeben. Der Kunde bekommt eine E-Mail.`);
    } catch (e) { setFehler(e instanceof ApiFehler ? e.message : 'Freigabe fehlgeschlagen.'); }
    finally { setLaeuft(false); }
  }

  return (
    <div>
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Datenkopie</p>
      {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
      <p className="mb-2 text-sm text-slate-600 dark:text-slate-400">
        {k.angefordertAm ? `Angefordert am ${tagDe(k.angefordertAm)}.` : 'Noch nicht angefordert.'}
        {k.freigabe && ` Freigegeben am ${tagDe(k.freigabe.am)} (${k.freigabe.formatText}).`}
        {k.zuletztGeladen && ` Heruntergeladen am ${tagDe(k.zuletztGeladen.am)}.`}
        {k.exportBis && ` Möglich bis ${tagDe(k.exportBis)}.`}
      </p>
      {k.moeglich && (
        <div className="flex flex-wrap items-end gap-2">
          <Auswahl label="Format" value={format} onChange={e => setFormat(e.target.value as 'lesbar' | 'wiederherstellung')}>
            <option value="lesbar">PDF und Tabellen (Excel) — zum Aufbewahren</option>
            <option value="wiederherstellung">Wiederherstellungsdatei — zum Einspielen</option>
          </Auswahl>
          <Knopf className="!text-xs" laedt={laeuft} onClick={freigeben}>{k.freigabe ? 'Format ändern' : 'Freigeben'}</Knopf>
        </div>
      )}
    </div>
  );
}

/** Gratismonat verlängern oder beenden — z. B. als Entgegenkommen im Support. */
function Gratismonat({ mandant, beiErfolg }: { mandant: Mandant; beiErfolg: (text: string) => void | Promise<void> }) {
  const tb = mandant.abo?.testBis;
  // Eingabe ist der letzte Gratistag; gespeichert wird der erste Tag, der wieder kostet.
  const [bis, setBis] = useState(() => (tb ? new Date(Date.parse(tb) - 1).toLocaleDateString('en-CA', { timeZone: 'Europe/Berlin' }) : ''));
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  async function speichern(ende: boolean) {
    setLaeuft(true);
    setFehler(null);
    try {
      let naechster: string | null = null;
      if (!ende) {
        const [j, mo, t] = bis.split('-').map(Number);
        naechster = new Date(Date.UTC(j, mo - 1, t + 1)).toISOString().slice(0, 10);
      }
      await api(`/billing/admin/tenants/${mandant.id}/gratis`, { methode: 'POST', daten: { bis: naechster } });
      await beiErfolg(ende ? `${mandant.name}: Gratismonat beendet.` : `${mandant.name}: gratis bis einschließlich ${new Date(bis).toLocaleDateString('de-DE')}.`);
    } catch (e) { setFehler(e instanceof ApiFehler ? e.message : 'Speichern fehlgeschlagen.'); }
    finally { setLaeuft(false); }
  }

  return (
    <div>
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Gratismonat</p>
      {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
      <div className="flex flex-wrap items-end gap-2">
        <Feld label="Gratis bis einschließlich" type="date" value={bis} onChange={e => setBis(e.target.value)} />
        <Knopf className="!text-xs" laedt={laeuft} disabled={!bis} onClick={() => speichern(false)}>Speichern</Knopf>
        {tb && <Knopf variante="still" className="!text-xs" laedt={laeuft} onClick={() => speichern(true)}>Gratismonat beenden</Knopf>}
      </div>
    </div>
  );
}

/** Die Sonderkondition, die gerade gilt — wie activeCustomOffer im Backend. */
function aktivesAngebot(liste: Angebot[], tenantId: string): Angebot | null {
  const jetzt = Date.now();
  return liste
    .filter(a => a.tenantId === tenantId && a.status === 'accepted')
    .filter(a => !a.activeUntil || new Date(a.activeUntil).getTime() > jetzt)
    .sort((a, b) => new Date(b.acceptedAt || 0).getTime() - new Date(a.acceptedAt || 0).getTime())[0] ?? null;
}

export default function Mandanten() {
  const { daten: mandanten, laedt, fehler, neuLaden } = useDaten<Mandant[]>('/billing/admin/tenants');
  const { daten: angebote, neuLaden: angeboteNeu } = useDaten<Angebot[]>('/billing/admin/offers');
  const { daten: alleBenutzer, neuLaden: benutzerNeu } = useDaten<Benutzer[]>('/users?all=1');
  const { daten: statistik } = useDaten<Statistik>('/billing/admin/stats');
  const { daten: sicherungen, neuLaden: sicherungenNeu } =
    useDaten<{ archive: Sicherung[]; aufbewahrungTage: number }>('/billing/admin/archive');

  const [offen, setOffen] = useState<string | null>(null);
  const [suche, setSuche] = useState('');
  const [meldung, setMeldung] = useState<string | null>(null);
  const [sendeFehler, setSendeFehler] = useState<string | null>(null);
  const [beschaeftigt, setBeschaeftigt] = useState<string | null>(null);
  const [neuesPasswort, setNeuesPasswort] = useState<{ benutzer: string; passwort: string } | null>(null);
  const [angebotFuer, setAngebotFuer] = useState<Mandant | null>(null);
  const [anschreibenFuer, setAnschreibenFuer] = useState<Mandant | null>(null);
  const [anlage, setAnlage] = useState<Record<string, string> | null>(null);
  const [neuerZugang, setNeuerZugang] = useState<{ firma: string; benutzername: string; passwort: string } | null>(null);
  // Zielkonto je Sicherung. Standard: das Konto, aus dem sie stammt.
  const [ziele, setZiele] = useState<Record<string, string>>({});

  if (laedt && !mandanten) return <div className="flex justify-center py-16"><Spinner /></div>;

  const gefiltert = (mandanten ?? []).filter(m => {
    if (!suche.trim()) return true;
    const s = suche.toLowerCase();
    return m.name.toLowerCase().includes(s) || (m.email || '').toLowerCase().includes(s);
  });

  const eingefroren = (mandanten ?? []).filter(m => m.status === 'frozen').length;
  const ohneSepa = (mandanten ?? []).filter(m => !m.isOwner && !m.hasSepa).length;

  // Die App verschickt keine E-Mails. Der Zugang muss deshalb persoenlich
  // uebergeben werden — dieses Blatt ist die Vorlage dafuer: ausdrucken oder als
  // PDF sichern, dann per Telefon/Post/persoenlich aushaendigen. Das Passwort
  // bewusst NICHT in eine Mail kopieren; es steht hier nur einmal.
  function uebergabeblattDrucken(zugang: { firma: string; benutzername: string; passwort: string }) {
    const esc = (t: string) => t.replace(/[<>&]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c] || c));
    const adresse = window.location.origin;
    const datum = new Date().toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
    const html = `<!doctype html><html lang="de"><head><meta charset="utf-8">
<title>Zugangsdaten ${esc(zugang.firma)}</title><style>
  @page { size: A4; margin: 25mm; }
  /* Das Blatt ist Papier — es darf nie den Dunkelmodus des Browsers erben,
     sonst steht dunkle Schrift auf dunklem Grund in der Druckvorschau. */
  :root { color-scheme: light; }
  html, body { background: #ffffff; }
  body { font-family: -apple-system, "Segoe UI", Helvetica, sans-serif; color: #0f172a; line-height: 1.55; margin: 0; padding: 24px; }
  h1 { font-size: 22px; margin: 0 0 4px; }
  .sub { color: #64748b; font-size: 13px; margin-bottom: 28px; }
  .box { border: 2px solid #0f172a; border-radius: 8px; padding: 18px 22px; margin: 22px 0; }
  .zeile { display: flex; justify-content: space-between; align-items: baseline; padding: 9px 0; border-bottom: 1px solid #e2e8f0; }
  .zeile:last-child { border-bottom: 0; }
  .k { color: #64748b; font-size: 13px; }
  .v { font-family: "SF Mono", Menlo, Consolas, monospace; font-size: 17px; font-weight: 600; letter-spacing: .3px; }
  ol { padding-left: 20px; } li { margin-bottom: 7px; font-size: 14px; }
  .warn { border-left: 4px solid #d97706; background: #fffbeb; padding: 12px 16px; font-size: 13px; margin-top: 22px; }
  .fuss { margin-top: 34px; font-size: 11px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 10px; }
</style></head><body>
  <h1>Ihre Zugangsdaten zu LagerPro</h1>
  <div class="sub">${esc(zugang.firma)} &middot; ausgestellt am ${datum}</div>
  <div class="box">
    <div class="zeile"><span class="k">Adresse</span><span class="v">${esc(adresse)}</span></div>
    <div class="zeile"><span class="k">Benutzername</span><span class="v">${esc(zugang.benutzername)}</span></div>
    <div class="zeile"><span class="k">Startpasswort</span><span class="v">${esc(zugang.passwort)}</span></div>
  </div>
  <ol>
    <li>Adresse im Browser öffnen.</li>
    <li>Mit Benutzername und Startpasswort anmelden.</li>
    <li>Die App fordert Sie sofort auf, ein eigenes Passwort zu vergeben. Erst danach ist sie nutzbar.</li>
    <li>Weitere Mitarbeiter legen Sie anschließend unter <b>Team</b> selbst an.</li>
  </ol>
  <div class="warn"><b>Wichtig:</b> Das Startpasswort ist nur für die erste Anmeldung gedacht und
  einer weiteren Person bekannt. Geben Sie dieses Blatt nicht weiter und bewahren Sie es nach der
  Passwortänderung nicht auf.</div>
  <div class="fuss">LagerPro &middot; Dieses Blatt wurde einmalig erzeugt. Das Startpasswort lässt sich nicht erneut anzeigen.</div>
</body></html>`;

    // Bewusst ein verstecktes iframe statt window.open: Browser blockieren
    // Pop-ups auch dann, wenn ein Klick dahintersteckt, und der Owner stünde
    // ohne Blatt da. Das iframe druckt zuverlässig und räumt sich selbst auf.
    const rahmen = document.createElement('iframe');
    rahmen.setAttribute('aria-hidden', 'true');
    rahmen.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
    document.body.appendChild(rahmen);
    const dok = rahmen.contentDocument;
    if (!dok) { rahmen.remove(); setSendeFehler('Übergabeblatt konnte nicht erzeugt werden.'); return; }
    dok.open(); dok.write(html); dok.close();
    const drucken = () => {
      rahmen.contentWindow?.focus();
      rahmen.contentWindow?.print();
      // Erst nach dem Druckdialog entfernen — sofortiges Aufräumen bricht ihn ab.
      window.setTimeout(() => rahmen.remove(), 1000);
    };
    if (dok.readyState === 'complete') drucken();
    else rahmen.onload = drucken;
  }

  async function sofortGueltig(m: Mandant, a: Angebot) {
    if (a.monthlyPrice !== 0) return;
    setBeschaeftigt(a.id);
    setSendeFehler(null);
    try {
      await api(`/billing/admin/offers/${a.id}`, { methode: 'PATCH', daten: { status: 'accepted' } });
      await neuLaden();
      await angeboteNeu();
      setMeldung(`${m.name} nutzt LagerPro ab sofort kostenlos — ohne SEPA-Mandat.`);
    } catch (e) {
      setSendeFehler(e instanceof ApiFehler ? e.message : 'Angebot konnte nicht aktiviert werden.');
    } finally { setBeschaeftigt(null); }
  }

  async function tarifSetzen(m: Mandant, plan: string) {
    const grund = prompt(
      `Tarif von „${m.name}" auf ${plan} umstellen.\n\n`
      + 'Grund (wird protokolliert und dem Kunden angezeigt):', 'Telefonisch vereinbart');
    if (!grund) return;

    setBeschaeftigt(m.id);
    setSendeFehler(null);
    try {
      const r = await api<{ plan: string; angebotBeendet: string | null }>(
        `/billing/admin/tenants/${m.id}/plan`, { methode: 'POST', daten: { plan, grund } });
      await neuLaden();
      await angeboteNeu();
      setMeldung(`${m.name} ist jetzt auf ${r.plan}.`
        + (r.angebotBeendet ? ` Die Sonderkondition „${r.angebotBeendet}" wurde beendet.` : ''));
    } catch (e) {
      setSendeFehler(e instanceof ApiFehler ? e.message : 'Tarifwechsel fehlgeschlagen.');
    } finally { setBeschaeftigt(null); }
  }

  async function passwortZuruecksetzen(b: Benutzer) {
    const grund = prompt(
      `Passwort von „${b.name}" (${b.username}) zurücksetzen.\n\n`
      + 'Das alte Passwort wird dabei ungültig.\n\nGrund:', 'Kunde hat Passwort vergessen');
    if (!grund) return;

    setBeschaeftigt(b.id);
    setSendeFehler(null);
    try {
      const r = await api<{ benutzer: string; passwort: string }>(
        `/billing/admin/users/${b.id}/passwort`, { methode: 'POST', daten: { grund } });
      setNeuesPasswort({ benutzer: r.benutzer, passwort: r.passwort });
      setMeldung(null);
    } catch (e) {
      setSendeFehler(e instanceof ApiFehler ? e.message : 'Zurücksetzen fehlgeschlagen.');
    } finally { setBeschaeftigt(null); }
  }

  async function einfrieren(m: Mandant, frieren: boolean) {
    const grund = frieren
      ? prompt(`Zugang von „${m.name}" einfrieren.\n\nGrund (der Kunde sieht ihn):`, 'Zahlungsrückstand')
      : '';
    if (frieren && grund === null) return;

    setBeschaeftigt(m.id);
    setSendeFehler(null);
    try {
      await api(`/billing/admin/tenants/${m.id}/${frieren ? 'freeze' : 'unfreeze'}`,
        { methode: 'POST', daten: frieren ? { reason: grund } : {} });
      await neuLaden();
      setMeldung(frieren
        ? `${m.name} wurde eingefroren. Angemeldete Benutzer verlieren den Zugang sofort.`
        : `${m.name} ist wieder freigegeben.`);
    } catch (e) {
      setSendeFehler(e instanceof ApiFehler ? e.message : 'Aktion fehlgeschlagen.');
    } finally { setBeschaeftigt(null); }
  }

  async function mandantAnlegen(e: React.FormEvent) {
    e.preventDefault();
    if (!anlage) return;
    setSendeFehler(null);
    setBeschaeftigt('anlage');
    try {
      const r = await api<{ tenant: { name: string }; zugang: { benutzername: string; passwort: string } }>(
        '/billing/admin/tenants', { methode: 'POST', daten: anlage });
      setAnlage(null);
      setNeuerZugang({ firma: r.tenant.name, benutzername: r.zugang.benutzername, passwort: r.zugang.passwort });
      await Promise.all([neuLaden(), benutzerNeu()]);
    } catch (e) {
      setSendeFehler(e instanceof ApiFehler ? e.message : 'Mandant konnte nicht angelegt werden.');
    } finally { setBeschaeftigt(null); }
  }

  // Die Sicherung wird angemeldet geladen; ein einfacher Link könnte den
  // Authorization-Header nicht mitschicken.
  async function sicherungHerunterladen(s: Sicherung, art: 'kunde' | 'lesbar') {
    setSendeFehler(null);
    const zielId = ziele[s.id] && ziele[s.id] !== s.tenantId ? ziele[s.id] : null;
    const ziel = zielId ? (mandanten ?? []).find(m => m.id === zielId) : null;
    try {
      const pfad = art === 'kunde'
        ? apiUrl(`/api/billing/admin/archive/${s.id}/wiederherstellung${zielId ? `?ziel=${encodeURIComponent(zielId)}` : ''}`)
        : apiUrl(`/api/billing/admin/archive/${s.id}`);
      const antwort = await fetch(pfad, { headers: { Authorization: `Bearer ${token() ?? ''}` } });
      if (!antwort.ok) {
        const fehler = await antwort.json().catch(() => null);
        throw new Error(fehler?.error || String(antwort.status));
      }
      const blob = await antwort.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const name = s.tenantName.replace(/[^a-zA-Z0-9äöüÄÖÜß]+/g, '-');
      const zielName = ziel ? `-fuer-${ziel.name.replace(/[^a-zA-Z0-9äöüÄÖÜß]+/g, '-')}` : '';
      a.download = art === 'kunde'
        ? `${name}-${s.createdAt.slice(0, 10)}${zielName}.lpsicherung`
        : `sicherung-${name}-lesbar.json`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      setMeldung(art !== 'kunde'
        ? `Lesbare Fassung von ${s.tenantName} heruntergeladen. Der Zugriff wurde protokolliert.`
        : ziel
          ? `Datei für das Konto „${ziel.name}" heruntergeladen. Sie lässt sich nur dort einspielen — `
            + `„${s.tenantName}" bleibt, wie es ist. Schick sie dem Kunden; er spielt sie im Konto „${ziel.name}" `
            + 'unter Einstellungen → Sicherung wiederherstellen ein.'
          : `Datei für ${s.tenantName} heruntergeladen. Schick sie dem Kunden — er spielt sie unter `
            + 'Einstellungen → Sicherung wiederherstellen ein. Lesen oder verändern kann er sie nicht.');
    } catch (e) {
      setSendeFehler(e instanceof Error ? e.message : 'Sicherung konnte nicht geladen werden.');
    }
  }

  async function sicherungLoeschen(s: Sicherung) {
    if (!confirm(`Sicherung von „${s.tenantName}" endgültig löschen?\n\n`
      + 'Danach lassen sich die Daten dieses Kunden nicht mehr herausgeben.')) return;
    try {
      await api(`/billing/admin/archive/${s.id}`, { methode: 'DELETE' });
      await sicherungenNeu();
      setMeldung('Sicherung gelöscht.');
    } catch (e) {
      setSendeFehler(e instanceof ApiFehler ? e.message : 'Löschen fehlgeschlagen.');
    }
  }

  async function exportieren() {
    try {
      const antwort = await fetch(apiUrl('/api/billing/admin/export'), {
        headers: { Authorization: `Bearer ${token() ?? ''}` },
      });
      if (!antwort.ok) throw new Error(String(antwort.status));
      const blob = await antwort.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mandanten-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      setMeldung('Mandantenliste heruntergeladen.');
    } catch {
      setSendeFehler('Export fehlgeschlagen.');
    }
  }

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Mandanten</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            {mandanten?.length ?? 0} Kunden
            {eingefroren > 0 && <span className="text-rose-600 dark:text-rose-400"> · {eingefroren} eingefroren</span>}
            {ohneSepa > 0 && <span className="text-amber-600 dark:text-amber-400"> · {ohneSepa} ohne SEPA</span>}
          </p>
        </div>
        <div className="flex gap-2">
          {!anlage && (
            <Knopf variante="primaer" onClick={() => {
              setAnlage({ name: '', email: '', username: '', adminName: '', plan: 'starter', ort: '', phone: '' });
              setMeldung(null);
            }}>Mandant anlegen</Knopf>
          )}
          <Knopf onClick={exportieren}>Exportieren</Knopf>
        </div>
      </div>

      {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
      {/* Erfolg und Fehler kommen fast immer von einem Knopf tief in der
          Mandantenliste — sie müssen dem Blick folgen, nicht umgekehrt. */}
      {(sendeFehler || meldung) && (
        <Blickfang ausloeser={sendeFehler ?? meldung} klasse="space-y-3">
          {sendeFehler && <Hinweis art="fehler">{sendeFehler}</Hinweis>}
          <Erfolgsmeldung text={meldung} />
        </Blickfang>
      )}

      {/* Ein zurückgesetztes Passwort erscheint genau einmal — danach steht nur
          noch der Hash in der Datenbank. */}
      {neuesPasswort && (
        <Blickfang ausloeser={neuesPasswort.benutzer}>
        <Karte klasse="border-amber-400 dark:border-amber-700">
          <KartenKopf titel="Neues Passwort — einmalige Anzeige"
                      aktion={<Knopf onClick={() => setNeuesPasswort(null)}>Verstanden</Knopf>} />
          <div className="space-y-3 p-5">
            <p className="text-sm text-slate-700 dark:text-slate-300">
              Für <strong>{neuesPasswort.benutzer}</strong>:
            </p>
            <p className="select-all rounded-lg border border-slate-300 bg-slate-50 px-4 py-3 font-mono text-lg text-slate-900 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100">
              {neuesPasswort.passwort}
            </p>
            <Hinweis art="warnung">
              Jetzt notieren und dem Kunden mitteilen — telefonisch, nicht per E-Mail. Nach dem
              Schließen ist es nicht mehr abrufbar. Bei der nächsten Anmeldung verlangt die App
              automatisch ein neues, selbst gewähltes Passwort.
            </Hinweis>
          </div>
        </Karte>
        </Blickfang>
      )}

      {/* Startpasswort erscheint genau einmal — danach nur noch der Hash. */}
      {neuerZugang && (
        <Blickfang ausloeser={neuerZugang.benutzername}>
        <Karte klasse="border-emerald-400 dark:border-emerald-700">
          <KartenKopf titel={`Zugang für ${neuerZugang.firma}`}
                      aktion={<Knopf onClick={() => setNeuerZugang(null)}>Verstanden</Knopf>} />
          <div className="space-y-3 p-5">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">Benutzername</p>
                <p className="select-all font-mono text-lg text-slate-900 dark:text-slate-100">{neuerZugang.benutzername}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 dark:text-slate-400">Startpasswort</p>
                <p className="select-all font-mono text-lg text-slate-900 dark:text-slate-100">{neuerZugang.passwort}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Knopf variante="primaer" onClick={() => uebergabeblattDrucken(neuerZugang)}>
                Übergabeblatt drucken
              </Knopf>
              <Knopf onClick={() => {
                void navigator.clipboard?.writeText(
                  `LagerPro-Zugang für ${neuerZugang.firma}\n`
                  + `Adresse: ${window.location.origin}\n`
                  + `Benutzername: ${neuerZugang.benutzername}\n`
                  + `Startpasswort: ${neuerZugang.passwort}\n`
                  + 'Das Passwort muss bei der ersten Anmeldung geändert werden.');
                setMeldung('Zugangsdaten in die Zwischenablage kopiert.');
              }}>In Zwischenablage</Knopf>
            </div>
            <Hinweis art="warnung">
              Jetzt sichern und dem Kunden übergeben — am besten telefonisch oder als gedrucktes
              Blatt, nicht per E-Mail. Nach dem Schließen ist das Passwort nicht mehr abrufbar.
              Der Kunde muss es bei der ersten Anmeldung ändern; die App erzwingt das.
            </Hinweis>
          </div>
        </Karte>
        </Blickfang>
      )}

      {anlage && (
        <Karte klasse="border-marke-300 dark:border-marke-800">
          <KartenKopf titel="Neuen Mandanten anlegen"
                      hinweis="Zugang steht sofort — das Startpasswort wird erzeugt und einmalig angezeigt" />
          <form onSubmit={mandantAnlegen} className="space-y-4 p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <Feld label="Firmenname" value={anlage.name} required autoFocus
                    onChange={e => setAnlage(a => a && ({ ...a, name: e.target.value }))} />
              <Feld label="E-Mail" type="email" value={anlage.email} required
                    onChange={e => setAnlage(a => a && ({ ...a, email: e.target.value }))} />
              <Feld label="Ansprechpartner" value={anlage.adminName} required
                    hinweis="Wird als Administrator angelegt."
                    onChange={e => setAnlage(a => a && ({ ...a, adminName: e.target.value }))} />
              <Feld label="Benutzername" value={anlage.username} required
                    hinweis="Mindestens 3 Zeichen, für die Anmeldung."
                    onChange={e => setAnlage(a => a && ({ ...a, username: e.target.value }))} />
              <Auswahl label="Tarif" value={anlage.plan}
                       onChange={e => setAnlage(a => a && ({ ...a, plan: e.target.value }))}>
                {TARIFE.map(t => <option key={t.id} value={t.id}>{t.name} · {euro(t.preis)}</option>)}
              </Auswahl>
              <Feld label="Ort (optional)" value={anlage.ort}
                    onChange={e => setAnlage(a => a && ({ ...a, ort: e.target.value }))} />
              <Feld label="Telefon (optional)" value={anlage.phone}
                    onChange={e => setAnlage(a => a && ({ ...a, phone: e.target.value }))} />
            </div>
            <div className="flex gap-2">
              <Knopf type="submit" variante="primaer" laedt={beschaeftigt === 'anlage'}>Anlegen</Knopf>
              <Knopf type="button" onClick={() => { setAnlage(null); setSendeFehler(null); }}>Abbrechen</Knopf>
            </div>
          </form>
        </Karte>
      )}

      {statistik && (statistik.tenants || statistik.users) && (
        <div className="grid gap-3 sm:grid-cols-3">
          {[
            ['Mandanten', String(statistik.tenants ?? mandanten?.length ?? 0)],
            ['Benutzer', String(statistik.users ?? alleBenutzer?.length ?? 0)],
            ['Monatlicher Umsatz', statistik.mrr != null ? euro(statistik.mrr) : '—'],
          ].map(([t, w]) => (
            <Karte key={t} klasse="p-4">
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">{t}</p>
              <p className="mt-1 text-xl font-bold text-slate-900 dark:text-slate-100">{w}</p>
            </Karte>
          ))}
        </div>
      )}

      {angebotFuer && (
        <AngebotFormular
          mandant={angebotFuer}
          beiAbbruch={() => setAngebotFuer(null)}
          beiErfolg={async text => { setAngebotFuer(null); setMeldung(text); await angeboteNeu(); }}
        />
      )}

      {sicherungen && sicherungen.archive.length > 0 && (
        <Karte klasse="border-amber-300 dark:border-amber-900">
          <KartenKopf
            titel="Sicherungen zurückgesetzter Kunden"
            hinweis={`Verschlüsselt · verfallen nach ${sicherungen.aufbewahrungTage} Tagen von selbst`}
          />
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {sicherungen.archive.map(s => {
              const tageUebrig = Math.max(0, Math.ceil(
                (new Date(s.laeuftAbAm).getTime() - Date.now()) / 86400000));
              const inhalt = Object.entries(s.umfang)
                .filter(([k, n]) => n > 0 && k !== 'dateienAusgelassen')
                .map(([k, n]) => `${n} ${k}`)
                .join(' · ');
              // Die Datei ist Base64 — gut ein Drittel größer als die Sicherung.
              const dateiMb = (s.groesse * 1.34) / 1024 / 1024;
              const ohneDateien = (s.format ?? 1) < 2;
              const zielId = ziele[s.id] ?? s.tenantId;
              // Neueste Konten oben: das zweite Konto hat der Kunde meist gerade erst angelegt.
              const andereKonten = (mandanten ?? [])
                .filter(m => !m.isOwner && m.id !== s.tenantId)
                .sort((x, y) => String(y.createdAt).localeCompare(String(x.createdAt)));
              const ziel = zielId !== s.tenantId ? andereKonten.find(m => m.id === zielId) : null;
              const herkunftsTarif = (mandanten ?? []).find(m => m.id === s.tenantId)?.plan;
              const tarifName = (id?: string) => TARIFE.find(t => t.id === id)?.name ?? id ?? '—';
              return (
                <li key={s.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5">
                  <div className="min-w-[200px] flex-1">
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{s.tenantName}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {datum(s.createdAt)} · {inhalt || 'keine Bewegungsdaten'}
                      {s.grund && s.grund !== 'Daten zurückgesetzt' && ` · ${s.grund}`}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Datei ca. {dateiMb < 0.1 ? '< 0,1' : dateiMb.toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB
                      {ohneDateien && <span className="text-amber-600 dark:text-amber-400"> · älteres Format, Dateien (Belege, Fotos) fehlen</span>}
                      {!ohneDateien && (s.umfang.dateienAusgelassen ?? 0) > 0 && (
                        <span className="text-amber-600 dark:text-amber-400"> · {s.umfang.dateienAusgelassen} große Dateien nicht enthalten</span>
                      )}
                      {dateiMb > 25 && <span className="text-amber-600 dark:text-amber-400"> · zu groß für Mail und Support-Anhang</span>}
                    </p>
                  </div>
                  <Abzeichen art={tageUebrig <= 5 ? 'rot' : 'gelb'}>
                    noch {tageUebrig} {tageUebrig === 1 ? 'Tag' : 'Tage'}
                  </Abzeichen>
                  <label className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                    Einspielen in
                    <select value={zielId} onChange={e => setZiele({ ...ziele, [s.id]: e.target.value })}
                            className="max-w-[14rem] rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100">
                      <option value={s.tenantId}>dasselbe Konto (ersetzt den heutigen Stand)</option>
                      {andereKonten.length > 0 && (
                        <optgroup label="Zweites Konto — beide Stände bleiben">
                          {andereKonten.map(m => (
                            <option key={m.id} value={m.id}>{m.name} · seit {datum(m.createdAt)}</option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                  </label>
                  <Knopf variante="primaer" onClick={() => sicherungHerunterladen(s, 'kunde')}>Datei für den Kunden</Knopf>
                  <Knopf variante="still" className="!text-xs" onClick={() => sicherungHerunterladen(s, 'lesbar')}
                         title="Für Datenschutzanfragen — lässt sich nicht einspielen">
                    Lesbar (JSON)
                  </Knopf>
                  <Knopf variante="gefahr" onClick={() => sicherungLoeschen(s)}>Löschen</Knopf>
                  {ziel && (
                    <p className="w-full text-xs text-marke-700 dark:text-marke-300">
                      Die Datei passt nur ins Konto „{ziel.name}". „{s.tenantName}" bleibt unverändert — der Kunde hat danach beide Stände.
                      {herkunftsTarif && ziel.plan !== herkunftsTarif && (
                        <span className="block text-amber-700 dark:text-amber-400">
                          „{ziel.name}" hat den Tarif {tarifName(ziel.plan)}, „{s.tenantName}" {tarifName(herkunftsTarif)}.
                          Was der Tarif nicht enthält (etwa Buchhaltung und Fixkosten), wird eingespielt, ist dort aber
                          erst mit passendem Tarif sichtbar.
                        </span>
                      )}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
          <div className="border-t border-slate-200 px-5 py-3 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
            Will ein Kunde seine Daten zurück: „Datei für den Kunden" laden und ihm schicken, zum Beispiel als
            Anhang einer Support-Antwort. Er spielt sie selbst ein und bestätigt mit seinem Passwort. Die Datei
            ist verschlüsselt und passt nur in das Konto, das bei „Einspielen in" steht. Will er seinen heutigen
            Stand behalten, legt er ein zweites Konto an (eigener Benutzername, eigene E-Mail) — dann dieses
            Konto wählen. Jede Herausgabe wird protokolliert.
          </div>
        </Karte>
      )}

      <Karte>
        <div className="border-b border-slate-200 p-4 dark:border-slate-800">
          <Feld label="Suche" value={suche} placeholder="Name oder E-Mail …"
                onChange={e => setSuche(e.target.value)} />
        </div>

        {gefiltert.length === 0 ? (
          <LeerZustand titel="Keine Mandanten gefunden" />
        ) : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {gefiltert.map(m => {
              const offeneAngebote = (angebote ?? []).filter(a => a.tenantId === m.id && a.status === 'pending');
              const aktiv = aktivesAngebot(angebote ?? [], m.id);
              const benutzer = (alleBenutzer ?? []).filter(u => u.tenantId === m.id);
              const istOffen = offen === m.id;

              return (
                <li key={m.id}>
                  <button
                    onClick={() => setOffen(istOffen ? null : m.id)}
                    className="flex w-full flex-wrap items-center gap-3 px-5 py-3.5 text-start hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  >
                    <div className="min-w-[200px] flex-1">
                      <p className="font-medium text-slate-900 dark:text-slate-100">
                        {m.name}
                        {m.isOwner && <span className="ml-2 text-xs font-normal text-slate-500">(eigener Zugang)</span>}
                      </p>
                      <p className="truncate text-sm text-slate-500 dark:text-slate-400">
                        {m.email} · {benutzer.length} {benutzer.length === 1 ? 'Benutzer' : 'Benutzer'}
                        {m.hasSepa ? ` · SEPA ${m.sepaIban}` : ' · kein SEPA-Mandat'}
                      </p>
                    </div>
                    <Abzeichen art={m.plan === 'enterprise' ? 'blau' : m.plan === 'professional' ? 'gruen' : 'neutral'}>
                      {m.plan}
                    </Abzeichen>
                    {m.abo?.phase === 'test' && m.abo.testBis && <Abzeichen art="gruen">gratis bis {letzterTag(m.abo.testBis)}</Abzeichen>}
                    {m.abo?.phase === 'gekuendigt' && m.abo.kuendigungZum && <Abzeichen art="gelb">gekündigt zum {letzterTag(m.abo.kuendigungZum)}</Abzeichen>}
                    {m.abo?.phase === 'beendet' && <Abzeichen art="rot">Vertrag beendet</Abzeichen>}
                    {aktiv && <Abzeichen art={aktiv.monthlyPrice === 0 ? 'neutral' : 'blau'}>{aktiv.monthlyPrice === 0 ? 'kostenlos' : `${euro(aktiv.monthlyPrice)}/Monat`}</Abzeichen>}
                    {m.status === 'frozen' && <Abzeichen art="rot">eingefroren</Abzeichen>}
                    {offeneAngebote.length > 0 && <Abzeichen art="gelb">{offeneAngebote.length} Angebot offen</Abzeichen>}
                    <span aria-hidden className="text-slate-400">{istOffen ? '▴' : '▾'}</span>
                  </button>

                  {istOffen && !m.isOwner && (
                    <div className="space-y-4 border-t border-slate-200 bg-slate-50 px-5 py-4 dark:border-slate-800 dark:bg-slate-950/40">
                      {m.status === 'frozen' && m.frozenReason && (
                        <Hinweis art="warnung" titel="Zugang eingefroren">
                          Grund: {m.frozenReason}{m.frozenAt && ` · seit ${datum(m.frozenAt)}`}
                        </Hinweis>
                      )}

                      <div>
                        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Tarif</p>
                        <div className="flex flex-wrap gap-1.5">
                          {TARIFE.map(t => (
                            <Knopf
                              key={t.id}
                              variante={m.plan === t.id ? 'primaer' : 'sekundaer'}
                              className="!text-xs"
                              disabled={m.plan === t.id || beschaeftigt === m.id}
                              onClick={() => tarifSetzen(m, t.id)}
                            >
                              {t.name} · {euro(t.preis)}
                            </Knopf>
                          ))}
                          <Knopf variante="still" className="!text-xs" onClick={() => setAngebotFuer(m)}>
                            Sonderkondition
                          </Knopf>
                          <Knopf variante="still" className="!text-xs" onClick={() => setAnschreibenFuer(m)}>
                            Anschreiben
                          </Knopf>
                        </div>
                        {offeneAngebote.map(a => (
                          <div key={a.id} className="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
                            <span>Offen: „{a.label}" · {a.monthlyPrice === 0 ? 'kostenlos' : `${euro(a.monthlyPrice)}/Monat`} · wartet auf Zustimmung</span>
                            {a.monthlyPrice === 0 && (
                              <Knopf className="!text-xs" laedt={beschaeftigt === a.id} onClick={() => sofortGueltig(m, a)}>
                                Sofort gültig
                              </Knopf>
                            )}
                          </div>
                        ))}
                      </div>

                      <div>
                        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          Benutzer
                        </p>
                        {benutzer.length === 0 ? (
                          <p className="text-sm text-slate-500 dark:text-slate-400">Keine Benutzer</p>
                        ) : (
                          <ul className="divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white dark:divide-slate-800 dark:border-slate-800 dark:bg-slate-900">
                            {benutzer.map(b => (
                              <li key={b.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
                                <span className="min-w-0 flex-1">
                                  <span className="block truncate text-sm text-slate-900 dark:text-slate-100">{b.name}</span>
                                  <span className="block truncate text-xs text-slate-500 dark:text-slate-400">
                                    {b.username}{b.email ? ` · ${b.email}` : ''}
                                  </span>
                                </span>
                                {b.role === 'admin' ? <Abzeichen art="gruen">Admin</Abzeichen> : <Abzeichen>Mitarbeiter</Abzeichen>}
                                {b.status === 'frozen' && <Abzeichen art="rot">gesperrt</Abzeichen>}
                                <Knopf variante="still" className="!text-xs"
                                       laedt={beschaeftigt === b.id}
                                       onClick={() => passwortZuruecksetzen(b)}>
                                  Passwort zurücksetzen
                                </Knopf>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>

                      <Gratismonat mandant={m} beiErfolg={async text => { setMeldung(text); await neuLaden(); }} />
                      {m.kopie && <KopieFreigabe mandant={m} beiErfolg={async text => { setMeldung(text); await neuLaden(); }} />}

                      <KiKontingent mandant={m} />

                      <div>
                        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Zugang</p>
                        {m.status === 'frozen' ? (
                          <Knopf className="!text-xs" laedt={beschaeftigt === m.id} onClick={() => einfrieren(m, false)}>
                            Zugang freigeben
                          </Knopf>
                        ) : (
                          <Knopf variante="gefahr" className="!text-xs" laedt={beschaeftigt === m.id} onClick={() => einfrieren(m, true)}>
                            Zugang einfrieren
                          </Knopf>
                        )}
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Karte>

      {anschreibenFuer && (
        <MandantAnschreiben
          vorausgewaehlt={[anschreibenFuer.id]}
          beiAbbruch={() => setAnschreibenFuer(null)}
          beiFertig={text => {
            setAnschreibenFuer(null);
            setMeldung(`${text}. Der Verlauf steht unter Support.`);
          }}
        />
      )}
    </div>
  );
}

function AngebotFormular({ mandant, beiAbbruch, beiErfolg }: {
  mandant: Mandant;
  beiAbbruch: () => void;
  beiErfolg: (meldung: string) => void | Promise<void>;
}) {
  const [label, setLabel] = useState(`Sonderkondition ${mandant.name}`);
  const [preis, setPreis] = useState('39');
  const [basis, setBasis] = useState(mandant.plan || 'professional');
  const [laufzeit, setLaufzeit] = useState('');
  const [notiz, setNotiz] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);

  const kostenlos = Number(preis.replace(',', '.')) === 0;

  // sofort: nur bei 0 € — der Kunde verliert nichts, also braucht es seine
  // Zustimmung nicht. Ein Preis über 0 bleibt immer Sache des Kunden.
  async function absenden(e: React.FormEvent, sofort = false) {
    e.preventDefault();
    setFehler(null);
    const monatspreis = Number(preis.replace(',', '.'));
    if (!Number.isFinite(monatspreis) || monatspreis < 0) {
      setFehler('Bitte einen gültigen Monatspreis angeben.');
      return;
    }
    if (sofort && monatspreis !== 0) return;
    setSendet(true);
    try {
      const angebot = await api<Angebot>('/billing/admin/offers', {
        methode: 'POST',
        daten: {
          tenantId: mandant.id, label: label.trim(), basePlan: basis,
          monthlyPrice: monatspreis,
          durationMonths: laufzeit ? Number(laufzeit) : null,
          note: notiz.trim() || undefined,
        },
      });
      if (sofort) {
        await api(`/billing/admin/offers/${angebot.id}`, { methode: 'PATCH', daten: { status: 'accepted' } });
        await beiErfolg(`${mandant.name} nutzt LagerPro ab sofort kostenlos — ohne SEPA-Mandat.`);
        return;
      }
      await beiErfolg(`Angebot für ${mandant.name} hinterlegt. Der Kunde sieht es unter „Tarif".`);
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Angebot konnte nicht angelegt werden.');
    } finally { setSendet(false); }
  }

  return (
    <Blickfang>
    <Karte klasse="border-marke-300 dark:border-marke-800">
      <KartenKopf titel={`Sonderkondition für ${mandant.name}`}
                  hinweis="Der Kunde stimmt selbst zu — danach stellt sich sein Tarif automatisch um." />
      <form onSubmit={absenden} className="space-y-4 p-5">
        {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
        <div className="grid gap-4 sm:grid-cols-2">
          <Feld label="Bezeichnung" value={label} onChange={e => setLabel(e.target.value)} required autoFocus />
          <Feld label="Monatspreis in Euro" value={preis} onChange={e => setPreis(e.target.value)} inputMode="decimal" required />
          <Auswahl label="Grundtarif" value={basis} onChange={e => setBasis(e.target.value)}>
            {TARIFE.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </Auswahl>
          <Feld label="Laufzeit in Monaten (leer = unbefristet)" value={laufzeit} inputMode="numeric"
                onChange={e => setLaufzeit(e.target.value.replace(/\D/g, ''))} />
        </div>
        <Feld label="Notiz für den Kunden (optional)" value={notiz} onChange={e => setNotiz(e.target.value)} />
        <div className="flex gap-2">
          {kostenlos && (
            <Knopf type="button" variante="primaer" laedt={sendet} onClick={e => absenden(e, true)}>Sofort gültig</Knopf>
          )}
          <Knopf type="submit" variante={kostenlos ? 'sekundaer' : 'primaer'} laedt={sendet}>
            {kostenlos ? 'Nur anbieten' : 'Angebot hinterlegen'}
          </Knopf>
          <Knopf type="button" onClick={beiAbbruch}>Abbrechen</Knopf>
        </div>
        {kostenlos && (
          <p className="text-xs text-slate-500 dark:text-slate-400">
            „Sofort gültig" stellt den Kunden ohne seine Zustimmung auf 0 € um — er braucht dann kein SEPA-Mandat.
            Geht nur bei 0 €; einen Preis muss der Kunde immer selbst annehmen.
          </p>
        )}
      </form>
    </Karte>
    </Blickfang>
  );
}
